import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconAlertCircle, IconCheck, IconChevronRight, IconLock } from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';

/**
 * Phase 4.7 — Day sessions list. Click into a closed session for the
 * Z-Report + correction panel (AdminDayDetailPage).
 */

interface ApiSession {
  id: string;
  openedAt: string;
  closedAt: string | null;
  openingFloatCents: number;
  countedCashCents: number | null;
  expectedCashCents: number | null;
  varianceCents: number | null;
  closingFloatCents: number | null;
  openedBy: { id: string; name: string } | null;
  closedBy: { id: string; name: string } | null;
}

function fmtR(cents: number | null) {
  if (cents === null) return '—';
  return `R ${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' });
}

export default function AdminDaysPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ApiSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/day-sessions', { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setSessions(data.sessions ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AdminLayout title="Day sessions" subtitle="History of opens + closes with Z-Reports">
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <section className="rounded-lg bg-surface border border-border p-4">
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="text-13 italic text-gray-700">No day sessions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-13">
              <thead>
                <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Opened</th>
                  <th className="py-2 pr-3">Closed</th>
                  <th className="py-2 pr-3 text-right">Opening float</th>
                  <th className="py-2 pr-3 text-right">Counted</th>
                  <th className="py-2 pr-3 text-right">Variance</th>
                  <th className="py-2 pr-3 text-center">Status</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const open = !s.closedAt;
                  const v = s.varianceCents ?? 0;
                  return (
                    <tr
                      key={s.id}
                      onClick={() => navigate(`/admin/days/${s.id}`)}
                      className="border-t border-border-soft cursor-pointer hover:bg-light"
                    >
                      <td className="py-2.5 pr-3 font-bold text-heading">{fmtDate(s.openedAt)}</td>
                      <td className="py-2.5 pr-3 text-gray-700 tabular-nums">
                        {fmtTime(s.openedAt)}
                        {s.openedBy && <span className="ml-2 text-12">· {s.openedBy.name}</span>}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700 tabular-nums">
                        {s.closedAt ? (
                          <>
                            {fmtTime(s.closedAt)}
                            {s.closedBy && <span className="ml-2 text-12">· {s.closedBy.name}</span>}
                          </>
                        ) : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-heading tabular-nums">{fmtR(s.openingFloatCents)}</td>
                      <td className="py-2.5 pr-3 text-right text-heading tabular-nums">{fmtR(s.countedCashCents)}</td>
                      <td className={`py-2.5 pr-3 text-right font-bold tabular-nums ${v === 0 ? 'text-heading' : v > 0 ? 'text-success' : 'text-danger'}`}>
                        {s.varianceCents === null ? '—' : (v > 0 ? '+' : '') + fmtR(v)}
                      </td>
                      <td className="py-2.5 pr-3 text-center">
                        {open ? (
                          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-success-soft text-success text-11 font-bold uppercase tracking-wider">
                            Open
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-light text-gray-700 text-11 font-bold uppercase tracking-wider">
                            <IconCheck size={12} stroke={2.5} /> Closed
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <IconChevronRight size={16} stroke={2.25} className="text-gray-700" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-12 font-medium text-gray-700 mt-4 inline-flex items-center gap-1.5">
        <IconLock size={12} stroke={2.25} />
        Sessions become read-only once a later day is closed.
      </p>
    </AdminLayout>
  );
}
